import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'lodash';

import { noop } from 'utils/func';
import { getPassiveHandlerDefaultOptions } from 'utils/detectPassiveEvents';
import scrollTo from 'apps/catalogue/utils/scrollTo';

export default class HorizontalScrollList extends PureComponent {
  static propTypes = {
    children: PropTypes.node,
    hideArrowsWhenNoScroll: PropTypes.bool,
    mainClassName: PropTypes.string,
    onScroll: PropTypes.func,
    arrowLeft: PropTypes.node,
    arrowRight: PropTypes.node,
  };

  static defaultProps = {
    mainClassName: 'rs-relatedSearch',
    onScroll: noop,
    arrowLeft: '\u276e',
    arrowRight: '\u276f',
  };

  state = {
    alignedToItemIndex: 0,
  };

  componentDidMount() {
    window.addEventListener('resize', this.throttledUpdateUIOnResize, getPassiveHandlerDefaultOptions());
    this.scrollList.addEventListener('scroll', this.throttledUpdateUIOnScroll, getPassiveHandlerDefaultOptions());
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.throttledUpdateUIOnResize, getPassiveHandlerDefaultOptions());
    this.scrollList.removeEventListener('scroll', this.throttledUpdateUIOnScroll, getPassiveHandlerDefaultOptions());
  }

  onClickPrev = () => {
    const { scrollList } = this;
    const products = [...scrollList.children];

    // Find the first product that would fit in a view if it was scrolled to the left
    const firstNotVisibleItem = products.findIndex(product =>
      product.offsetLeft > (scrollList.scrollLeft - scrollList.offsetWidth)
    );

    const min = 0;

    this.setState({
      alignedToItemIndex: Math.max(firstNotVisibleItem, min),
    }, this.updateUI);
  };

  onClickNext = () => {
    const { scrollList } = this;
    const products = [...scrollList.children];

    // Find the first product that doesn't fit in current view
    const firstNotVisibleItem = products.findIndex(product => (
      (product.offsetLeft + product.offsetWidth) > (scrollList.offsetWidth + scrollList.scrollLeft))
    );

    const max = products.length - 1;

    this.setState({
      alignedToItemIndex: Math.min(firstNotVisibleItem, max),
    }, this.updateUI);
  };

  get scrollTo() {
    if (!this.scrollList) {
      return {};
    }

    const products = [...this.scrollList.children];
    const activeItem = products[this.state.alignedToItemIndex];

    if (!activeItem) {
      return null;
    }

    return activeItem.offsetLeft;
  }

  get prevDisabled() {
    if (!this.scrollList) {
      return true;
    }

    return Math.floor(this.scrollList.scrollLeft) === 0;
  }

  get nextDisabled() {
    if (!this.scrollList) {
      return true;
    }

    const { width } = this.scrollList.getBoundingClientRect();

    const maxScroll = Math.floor(this.scrollList.scrollWidth - width);
    const actualScroll = Math.floor(this.scrollList.scrollLeft);

    return actualScroll >= maxScroll;
  }

  setScrollListRef = ref => {
    this.scrollList = ref;
    this.forceUpdate();
  };

  resetUI = () => {
    this.setState({
      alignedToItemIndex: 0,
    }, this.updateUI);
  };

  updateUI = (scroll = true) => {
    if (scroll) {
      scrollTo({
        element: this.scrollList,
        to: this.scrollTo,
        callback: () => this.forceUpdate(),
      });
    } else {
      this.forceUpdate();
    }
  };

  throttledUpdateUIOnScroll = throttle(() => {
    this.props.onScroll();
    this.updateUI(false);
  }, 100);

  throttledUpdateUIOnResize = throttle(() => {
    this.updateUI(false);
  }, 100);

  renderList = () => {
    const { children, mainClassName } = this.props;

    if (Array.isArray(children)) {
      return (
        <ol
          className={`${mainClassName}__items`}
          ref={this.setScrollListRef}
        >
          {children}
        </ol>
      );
    }

    return React.cloneElement(children, { forwardedRef: this.setScrollListRef });
  };

  render() {
    const { mainClassName, hideArrowsWhenNoScroll, arrowLeft, arrowRight } = this.props;
    const hideScrollArrows = hideArrowsWhenNoScroll && this.prevDisabled && this.nextDisabled;

    return (
      <>
        {!hideScrollArrows && (
          <button
            className={`${mainClassName}__button ${mainClassName}__button--previous`}
            disabled={this.prevDisabled}
            onClick={this.onClickPrev}
            aria-label="Previous"
          >
            {arrowLeft}
          </button>
        )}
        <div className={`${mainClassName}__items-wrapper`}>
          {this.renderList()}
        </div>
        {!hideScrollArrows && (
          <button
            className={`${mainClassName}__button ${mainClassName}__button--next`}
            disabled={this.nextDisabled}
            onClick={this.onClickNext}
            aria-label="Next"
          >
            {arrowRight}
          </button>
        )}
      </>
    );
  }
}
